import { useState, useRef, useEffect } from 'react'
import { Filter, Layers, MoreHorizontal, Search, X, ChevronDown, FileDown, FileText } from 'lucide-react'
import { Avatar } from '../../../shared/components/ui/Avatar'
import { Tooltip } from '../../../shared/components/ui/Tooltip'
import type { UserOption } from '../../../shared/hooks/useUserOptions'
import { ISSUE_TYPE_OPTIONS, PRIORITY_OPTIONS } from '../boardUtils'

export type GroupByOption = 'none' | 'status' | 'assignee' | 'priority' | 'type' | 'sprint'

export interface IssueFilters {
  search: string
  type: string
  priority: string
  assigneeId: string
  groupBy: GroupByOption
}

export const EMPTY_ISSUE_FILTERS: IssueFilters = { search: '', type: '', priority: '', assigneeId: '', groupBy: 'none' }

export interface IssueFilterBarProps {
  filters: IssueFilters
  onChange: (filters: IssueFilters) => void
  members: UserOption[]
  statuses?: Array<{ id: string; name: string; color: string }>
  sprints?: Array<{ id: string; name: string }>
  /** Callback invoked when the user picks an export format. */
  onExport?: (format: 'excel' | 'pdf') => void
}

const AVATAR_COLORS: Record<string, string> = {
  'u-du': 'bg-[#006644] text-white',
  'u-sc': 'bg-[#0747A6] text-white',
  'u-mj': 'bg-[#0052CC] text-white',
  'u-pp': 'bg-[#00B8D9] text-white',
  'u-jl': 'bg-[#6554C0] text-white',
}

export function IssueFilterBar({ filters, onChange, members, statuses = [], sprints = [], onExport }: IssueFilterBarProps) {
  const [showFilterDropdown, setShowFilterDropdown] = useState(false)
  const [showGroupDropdown, setShowGroupDropdown] = useState(false)
  const [showMoreDropdown, setShowMoreDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const groupDropdownRef = useRef<HTMLDivElement>(null)
  const moreDropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowFilterDropdown(false)
      }
      if (groupDropdownRef.current && !groupDropdownRef.current.contains(e.target as Node)) {
        setShowGroupDropdown(false)
      }
      if (moreDropdownRef.current && !moreDropdownRef.current.contains(e.target as Node)) {
        setShowMoreDropdown(false)
      }
    }
    if (showFilterDropdown || showGroupDropdown || showMoreDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showFilterDropdown, showGroupDropdown, showMoreDropdown])

  const set = <K extends keyof IssueFilters>(key: K, value: IssueFilters[K]) =>
    onChange({ ...filters, [key]: value })

  const activeFiltersCount =
    (filters.type ? 1 : 0) + (filters.priority ? 1 : 0) + (filters.assigneeId ? 1 : 0)

  const toggleAssignee = (userId: string) => {
    if (filters.assigneeId === userId) {
      set('assigneeId', '')
    } else {
      set('assigneeId', userId)
    }
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
      {/* Left side filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search Input */}
        <div className="relative w-52">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search board..."
            value={filters.search}
            onChange={(e) => set('search', e.target.value)}
            className="h-8 w-full rounded-md border border-gray-200 bg-white pl-8 pr-7 text-xs text-gray-800 placeholder-gray-400 transition-colors focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500"
          />
          {filters.search && (
            <button
              onClick={() => set('search', '')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-slate-200"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Member Avatar Quick Filters */}
        <div className="flex items-center -space-x-1.5 pl-0.5">
          {members.slice(0, 5).map((member) => {
            const isSelected = filters.assigneeId === member.id
            const initials = member.display_name
              .split(' ')
              .map((n) => n[0])
              .join('')
              .toUpperCase()
              .slice(0, 2)
            const colorClass = AVATAR_COLORS[member.id] || 'bg-blue-600 text-white'

            return (
              <Tooltip key={member.id} content={member.display_name}>
                <button
                  type="button"
                  onClick={() => toggleAssignee(member.id)}
                  className={`relative flex h-6 w-6 items-center justify-center rounded-full border-2 border-white text-[10px] font-bold shadow-xs transition-transform hover:z-10 hover:scale-105 dark:border-slate-900 ${colorClass} ${
                    isSelected ? 'ring-2 ring-blue-600 ring-offset-1 z-10' : ''
                  }`}
                >
                  {initials}
                </button>
              </Tooltip>
            )
          })}
        </div>

        {/* Filter Button with Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            className={`flex h-8 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 ${
              activeFiltersCount > 0 || showFilterDropdown
                ? 'border-blue-500 bg-blue-50/50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                : ''
            }`}
          >
            <Filter className="h-3.5 w-3.5 text-gray-500" />
            <span>Filter</span>
            {activeFiltersCount > 0 && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">
                {activeFiltersCount}
              </span>
            )}
          </button>

          {showFilterDropdown && (
            <div className="absolute left-0 top-full z-20 mt-1 w-64 rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-slate-700 dark:bg-slate-800">
              <div className="space-y-3">
                {/* Type Filter */}
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-slate-400">Issue Type</label>
                  <select
                    value={filters.type}
                    onChange={(e) => set('type', e.target.value)}
                    className="w-full rounded border border-gray-200 bg-white p-1 text-xs text-gray-700 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                  >
                    <option value="">All Types</option>
                    {ISSUE_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Priority Filter */}
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-slate-400">Priority</label>
                  <select
                    value={filters.priority}
                    onChange={(e) => set('priority', e.target.value)}
                    className="w-full rounded border border-gray-200 bg-white p-1 text-xs text-gray-700 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                  >
                    <option value="">All Priorities</option>
                    {PRIORITY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {activeFiltersCount > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      onChange(EMPTY_ISSUE_FILTERS)
                      setShowFilterDropdown(false)
                    }}
                    className="w-full rounded bg-gray-100 py-1 text-center text-xs font-medium text-gray-600 hover:bg-gray-200 dark:bg-slate-700 dark:text-slate-300"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Group Dropdown */}
        <div className="relative" ref={groupDropdownRef}>
          <button
            type="button"
            onClick={() => setShowGroupDropdown(!showGroupDropdown)}
            className={`flex h-8 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 ${
              filters.groupBy !== 'none' || showGroupDropdown
                ? 'border-blue-500 bg-blue-50/50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                : ''
            }`}
          >
            <Layers className="h-3.5 w-3.5 text-gray-500" />
            <span>Group</span>
            {filters.groupBy !== 'none' && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">
                •
              </span>
            )}
            <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
          </button>

          {showGroupDropdown && (
            <div className="absolute left-0 top-full z-20 mt-1 w-48 rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-slate-700 dark:bg-slate-800">
              <div className="space-y-1">
                {[
                  { value: 'none', label: 'None' },
                  { value: 'status', label: 'Status' },
                  { value: 'assignee', label: 'Assignee' },
                  { value: 'priority', label: 'Priority' },
                  { value: 'type', label: 'Issue Type' },
                  { value: 'sprint', label: 'Sprint' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      set('groupBy', opt.value as GroupByOption)
                      setShowGroupDropdown(false)
                    }}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs text-gray-700 rounded hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-700 ${
                      filters.groupBy === opt.value ? 'bg-blue-50 font-medium text-blue-700 dark:bg-blue-950/40 dark:text-blue-300' : ''
                    }`}
                  >
                    {filters.groupBy === opt.value && <span className="h-4 w-4 text-blue-600">✓</span>}
                    <span className="flex-1 text-left">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Clear Filters pill if any filter is set */}
        {(filters.search || activeFiltersCount > 0) && (
          <button
            type="button"
            onClick={() => onChange(EMPTY_ISSUE_FILTERS)}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-900 dark:text-slate-400 dark:hover:text-slate-200"
          >
            <X className="h-3.5 w-3.5" />
            <span>Clear</span>
          </button>
        )}
      </div>

      {/* Right side utility icons */}
      <div className="flex items-center gap-1.5">
        <div className="relative" ref={moreDropdownRef}>
          <button
            type="button"
            onClick={() => setShowMoreDropdown((v) => !v)}
            title="More options"
            className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>

          {showMoreDropdown && (
            <div
              onClick={(e) => e.stopPropagation()}
              className="absolute right-0 top-full z-20 mt-1 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-800"
            >
              <button
                type="button"
                onClick={() => {
                  setShowMoreDropdown(false)
                  onExport?.('excel')
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                <FileDown className="h-3.5 w-3.5 text-gray-500" />
                <span>Export to Excel</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowMoreDropdown(false)
                  onExport?.('pdf')
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                <FileText className="h-3.5 w-3.5 text-gray-500" />
                <span>Export to PDF</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export function applyIssueFilters<T extends { key?: string; title: string; type?: string; priority?: string; assignee_id?: any }>(

  issues: T[],
  filters: IssueFilters,
  getAssigneeId?: (assignee: any) => string,
): T[] {
  const q = filters.search.trim().toLowerCase()
  return issues.filter((issue) => {
    if (q) {
      const matchKey = (issue.key || '').toLowerCase().includes(q)
      const matchTitle = (issue.title || '').toLowerCase().includes(q)
      if (!matchKey && !matchTitle) return false
    }
    if (filters.type && issue.type !== filters.type) return false
    if (filters.priority && issue.priority !== filters.priority) return false
    if (filters.assigneeId) {
      const assigneeId = getAssigneeId ? getAssigneeId(issue.assignee_id) : (issue.assignee_id || '')
      if (filters.assigneeId === 'unassigned' ? !!assigneeId : assigneeId !== filters.assigneeId) return false
    }
    return true
  })
}
